import type { ReactElement } from 'react'
import { ButtonSave, NotReady, Page, WorkflowDeprecatedCheck } from '@components'
import {
  isEmoji,
  isMaterialSymbol,
  useAddDisposition,
  useDispositions,
  useMultiAccountFeature,
  useProfile,
  useTranslation,
} from '@front/model'
import { useToastError } from '@hooks'
import { hasLength, isNotEmpty, useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useNavigate, useRouter, createFileRoute } from '@tanstack/react-router'
import type { UnsavedDisposition } from '@terros/common'
import { parseAsBoolean, useQueryState } from 'nuqs'
import { DispositionForm } from './DispositionForm'
import type { DispositionFormData } from './types'
import { fromDispositionFormData, toDispositionFormData } from './util'

function AddDisposition(): ReactElement {
  const { data, isLoading, error } = useDispositions()
  const { user } = useProfile()
  const [isParent] = useQueryState('isParent', parseAsBoolean.withDefault(false))

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='Dispositions' isLoading={isLoading} />
  if (!user) return <NotReady isLoading id='Profile' />

  return (
    <WorkflowDeprecatedCheck deprecateIfWorkflowEnabled>
      <AddDispositionWithDispositionData
        data={{
          scope: 'company',
          scopeId: user.companyId,
          name: '',
          order: (data.at(-1)?.order ?? 0) + 1_000_000_000,
          color: '',
          code: '',
          icon: '',
          createAccount: false,
          scheduleAppointment: false,
          parentOnly: isParent,
          interaction: false,
          pitch: false,
        }}
      />
    </WorkflowDeprecatedCheck>
  )
}

const AddDispositionWithDispositionData = ({ data }: { data: UnsavedDisposition }): ReactElement => {
  const { t } = useTranslation()
  const isMultiAccountEnabled = useMultiAccountFeature().isEnabled
  const { add, isAdding, error: addError } = useAddDisposition()
  useToastError(addError)
  const navigate = useNavigate()
  const router = useRouter()
  const handleSubmit = async (unsaved: DispositionFormData<UnsavedDisposition>): Promise<void> => {
    // makes sure parentOnly is not accidentally set to true if multi-account is not enabled
    if (!isMultiAccountEnabled) {
      unsaved.parentOnly = false
    }
    // makes sure the disposition scope is for company only if parentOnly is true
    if (unsaved.scope !== 'company' && unsaved.parentOnly) {
      unsaved.parentOnly = false
    }
    if (isMultiAccountEnabled && unsaved.parentOnly) {
      unsaved.scope = 'company'
    }

    await add(fromDispositionFormData(unsaved))
    router.history.back()
    form.resetDirty(unsaved)
    notifications.show({ message: t('Disposition added'), color: 'green' })
  }
  const form = useForm<DispositionFormData<UnsavedDisposition>>({
    initialValues: toDispositionFormData(data),
    validate: {
      name: isNotEmpty(t('Name cannot be empty')),
      code: hasLength({ min: 1, max: 4 }, t('Code must be 1-4 characters')),
      color: isNotEmpty(t('Color cannot be empty')),
      icon: (val) => (isEmoji(val) || isMaterialSymbol(val) ? null : t('Icon must be a single emoji or selected icon')),
    },
  })
  return (
    <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
      <Page>
        <Page.Heading back goBackBehavior='history' title={t('Add Disposition')}>
          <ButtonSave disabled={!form.isDirty()} loading={isAdding} type='submit' />
        </Page.Heading>
        <Page.Content scrollArea>
          <DispositionForm form={form} />
        </Page.Content>
      </Page>
    </form>
  )
}

export const Route = createFileRoute('/settings/disposition_/add/')({
  component: AddDisposition,
})
